/* SLOVNÍK: PRODUKT → IKONA (+ barva rodiny).
 * ============================================================================
 *
 * Náhrada emoji z lib/contractMeta.js (CONTRACT_TYPE_ICON) a z Radaru
 * příležitostí (FAMILY_META). Dnes tam 🏦 znamená čtyři různé produkty
 * (penze, úvěr, hypotéka, stavební spoření) a 🛡️ čtyři další věci — tady má
 * každý produkt JEDNU vlastní ikonu a stejnou všude.
 *
 * Klíče = `product_type` z databáze (stejné jako CONTRACT_TYPE_LABEL), aby šlo
 * psát `ikonaProduktu(smlouva.type)` bez převodní tabulky. Radar má vlastní
 * jména rodin → `RODINY_RADARU` je převádí na produkt.
 *
 * Rodiny barev (tóny z lib/ikony/tony.js):
 *   zajisteni  životní a zdravotní pojištění
 *   majetek    pojištění majetku a auta
 *   uvery      hypotéka, úvěr
 *   sporeni    penze, stavební spoření, rezerva
 *   investice  investice
 *   proces     analýza
 *   neutralni  ostatní
 *
 * Zatím se NEPOUŽÍVÁ na obrazovkách — je to stavebnice pro výměnu emoji
 * (vlna V1: klientské výstupy). Ukázka: /design-lab/vzorkovna.
 */
import {
  KeyRound, CreditCard, BrickWall, PiggyBank, ChartLine, HeartPulse,
  Stethoscope, LifeBuoy, ClipboardList, FileText, Shield, Scale, TrendingUp,
  Umbrella, ShieldEllipsis,
} from "lucide-react";
import { AutoNehoda, DumSeStitem } from "@/components/ikony/doplnky";
import { zaznam, vyber } from "@/lib/ikony/zaznam";

export const IKONY_PRODUKTU = Object.freeze({
  hypoteka: zaznam(KeyRound, "uvery", "hypoteka", "ikony.produkt.hypoteka"),
  uver: zaznam(CreditCard, "uvery", "uver", "ikony.produkt.uver"),
  stavebni: zaznam(BrickWall, "sporeni", "stavebni_sporeni", "ikony.produkt.stavebni"),
  penzijko: zaznam(PiggyBank, "sporeni", "penze", "ikony.produkt.penzijko"),
  investice: zaznam(ChartLine, "investice", "investice", "ikony.produkt.investice"),
  pojisteni_zivot: zaznam(HeartPulse, "zajisteni", "zivotni_pojisteni", "ikony.produkt.pojisteni_zivot"),
  zdravotni: zaznam(Stethoscope, "zajisteni", "zdravotni_pojisteni", "ikony.produkt.zdravotni"),
  pojisteni_majetek: zaznam(DumSeStitem, "majetek", "pojisteni_majetku", "ikony.produkt.pojisteni_majetek"),
  pojisteni_auta: zaznam(AutoNehoda, "majetek", "pojisteni_auta", "ikony.produkt.pojisteni_auta"),
  /* Od 4. 10. 2026 vlastní typy smluv (dřív „Jiné“). Deštník = pojištění
   * odpovědnosti (kryje škodu, kterou klient způsobí jiným); štít se třemi
   * tečkami = ostatní neživotní (právní ochrana, cestovní, podnikatelské). */
  pojisteni_odpovednost: zaznam(Umbrella, "majetek", "pojisteni_odpovednosti", "ikony.produkt.pojisteni_odpovednost"),
  pojisteni_ostatni: zaznam(ShieldEllipsis, "majetek", "pojisteni_ostatni", "ikony.produkt.pojisteni_ostatni"),
  rezerva: zaznam(LifeBuoy, "sporeni", "rezerva", "ikony.produkt.rezerva"),
  analyza: zaznam(ClipboardList, "proces", "analyza", "ikony.produkt.analyza"),
  jine: zaznam(FileText, "neutralni", "jine", "ikony.produkt.jine"),
});

/** Rodina mezery v Radaru příležitostí (klíče FAMILY_META) → produkt výše. */
export const RODINY_RADARU = Object.freeze({
  life: "pojisteni_zivot",
  pension: "penzijko",
  investment: "investice",
  property_ins: "pojisteni_majetek",
  car: "pojisteni_auta",
  health: "zdravotni",
  mortgage: "hypoteka",
  credit: "uver",
  rezerva: "rezerva",
  analyza: "analyza",
  other: "jine",
});

/** Investiční profil klienta. Dynamický = rostoucí čára, NE raketa
 *  (raketa u investic vyznívá jako slib výnosu). */
export const PROFILY_INVESTORA = Object.freeze({
  konzervativni: zaznam(Shield, "sporeni", "profil_konzervativni", "ikony.profil.konzervativni"),
  vyvazeny: zaznam(Scale, "sporeni", "profil_vyvazeny", "ikony.profil.vyvazeny"),
  dynamicky: zaznam(TrendingUp, "sporeni", "profil_dynamicky", "ikony.profil.dynamicky"),
});

/** Záznam pro typ smlouvy / produktu. Neznámý typ → „Jiné" (nikdy prázdno). */
export function ikonaProduktu(typ) {
  return vyber(IKONY_PRODUKTU, typ, "jine");
}

/** Záznam pro rodinu Radaru příležitostí. Neznámá rodina → „Jiné". */
export function ikonaRodinyRadaru(rodina) {
  const k = rodina == null ? "" : String(rodina);
  return ikonaProduktu(Object.prototype.hasOwnProperty.call(RODINY_RADARU, k) ? RODINY_RADARU[k] : "jine");
}

/** Záznam investičního profilu. Neznámý profil → vyvážený. */
export function ikonaProfilu(profil) {
  return vyber(PROFILY_INVESTORA, profil, "vyvazeny");
}
